import bg from '@/assets/img/bg1.jpg'
import { useActions, useAppSelector } from '@/hooks'
import Chart from '@/modules/device-module/components/chart/Chart'
import Description from '@/modules/device-module/components/description/Description'
import Features from '@/modules/device-module/components/features/Features'
import FeaturesPhoto from '@/modules/device-module/components/features/FeaturesPhoto'
import FeaturesVideo from '@/modules/device-module/components/features/FeaturesVideo'
import FeaturesLightning from '@/modules/device-module/components/features/FeaturesLightning'
import FeaturesDit from '@/modules/device-module/components/features/FeaturesDit'
import Gallery from '@/components/Organisms/Gallery'
import TableMeteo from '@/modules/device-module/components/meteo/TableMeteo'
import TableDevice from '@/modules/device-module/components/table/TableDevice'
import Terminal from '@/modules/device-module/components/terminal/Terminal'
import LazyVideoComponent from '@/modules/device-module/components/video'
import DIT from '@/pages/DIT'
import {
  useGetDeviceDetailIntervalQuery,
  useGetDeviceDetailQuery,
  useGetPhotosDetailQuery,
} from '@/modules/device-module/services/deviceService'
import { stateDeviceSelector } from '@/modules/device-module/store/stateSlice'
import { Status } from '@/modules/device-module/types/deviceType'
import { filterSystemSelector } from '@/store/filterSystemsSlise'
import cn from '@/utils/cn'
import { Box, CircularProgress, Paper } from '@mui/material'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Actions from './components/actions/Actions'
import Events from './components/events/Events'
import Header from './components/header/Header'
import FeaturesDTP from './components/features/FeaturesDTP'
import { DeviceType } from '@/types/enums'
import { DeviceTab } from '@/modules/device-module/store/stateSlice'
import ChartsDetector from '@/modules/device-module/components/features/ChartsDetector'
import FeaturesDetector from '@/modules/device-module/components/features/FeaturesDetector'

const moduleClassName = cn('device-module')

const DeviceModule = () => {
  const { id } = useParams()
  const { pathname } = useLocation()

  const [loading, setLoading] = useState(false)
  const [skipFirstQuery, setSkipFirstQuery] = useState(true)
  const { setFooterCounters } = useActions()
  const { selectedTab, device, isOnChange } =
    useAppSelector(stateDeviceSelector)

  const isPhotosPage = pathname.includes('photos')
  const skipDeviceQuery = isPhotosPage || isOnChange

  const { data, isLoading } = useGetDeviceDetailQuery(Number(id), {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    pollingInterval: 60000 * 3,
    skip: skipDeviceQuery,
  })

  useGetDeviceDetailIntervalQuery(Number(id), {
    pollingInterval: 60000,
    skip: isPhotosPage || skipFirstQuery,
  })

  const { data: photosData, isLoading: isLoadingPhotos } =
    useGetPhotosDetailQuery(Number(id), {
      skip: !isPhotosPage,
      refetchOnMountOrArgChange: true,
    })

  const {
    setSelected,
    setSelectedDevice,
    setCommands,
    setLastUpdatedDate,
    setTerminalHistoryNew,
  } = useActions()

  const { isFullScreen: isFullScreenGlobal, isFullScreenDetail } =
    useAppSelector(filterSystemSelector)

  const isPageLoading = isLoading || loading || isLoadingPhotos
  const isPhotoDevice = data?.typeId === DeviceType.photo
  const isVideoDevice = data?.typeId === DeviceType.video
  const isDitDevice = data?.typeId === DeviceType.dit
  const isMeteoDevice = data?.typeId === DeviceType.meteo
  const isDetectorDevice = data?.typeId === DeviceType.detector
  const isPhotosData = photosData?.typeId === DeviceType.photo
  const isMediaDevice =
    isPhotoDevice || isVideoDevice || isDitDevice || isPhotosData
  const isDefaultDevicesView =
    selectedTab === DeviceTab.devices &&
    !isPhotoDevice &&
    !isVideoDevice &&
    !isPhotosData

  useEffect(() => {
    const modules = isMeteoDevice ? device?.modulesMeteo : device?.modules
    if (modules) {
      setFooterCounters({
        totalErrors: modules.filter((item) => item.state === Status.error)
          .length,
        totalWarnings: modules.filter((item) => item.state === Status.warning)
          .length,
        totalServiceable: modules.filter((item) => item.state === Status.ok)
          .length,
      })
    }
  }, [device])

  useEffect(() => {
    setSelectedDevice(true)
    setSelected([])
    setCommands([])
    setLastUpdatedDate(new Date('1900').toISOString())
    setTerminalHistoryNew([])
  }, [
    id,
    setSelectedDevice,
    setSelected,
    setCommands,
    setLastUpdatedDate,
    setTerminalHistoryNew,
  ])

  useEffect(() => {
    const timer = setTimeout(() => setSkipFirstQuery(false), 0)
    return () => clearTimeout(timer)
  }, [])

  const outerBoxHeight = {
    xss: isDefaultDevicesView ? '133%' : '100%',
    md: '100%',
  }

  const contentBoxHeight = {
    xss:
      selectedTab === DeviceTab.description
        ? '95%'
        : selectedTab === DeviceTab.terminal
          ? 'calc(100% - 65px)'
          : 'calc(100% - 127px)',
    sm:
      selectedTab === DeviceTab.description
        ? '95%'
        : selectedTab === DeviceTab.terminal
          ? 'calc(100% - 65px)'
          : 'calc(100% - 100px)',
    lg: 'calc(100% - 48px)',
  }

  const renderTabContent = () => {
    if (selectedTab === DeviceTab.devices) {
      if (isDetectorDevice) {
        return <ChartsDetector />
      }
      return (
        <>
          {isMeteoDevice && <TableMeteo setLoading={setLoading} />}
          {(isPhotoDevice || isPhotosData) && <Gallery />}
          {isVideoDevice && <LazyVideoComponent />}
          {isDitDevice && <DIT />}
          {!isMeteoDevice && !isMediaDevice && (
            <TableDevice setLoading={setLoading} />
          )}
          {!isMediaDevice && <Chart />}
        </>
      )
    }
    if (selectedTab === DeviceTab.events) return <Events />
    if (selectedTab === DeviceTab.description) return <Description />
    if (selectedTab === DeviceTab.properties) {
      return (
        <>
          {(isPhotoDevice || isPhotosData) && <FeaturesPhoto />}
          {data?.typeId === DeviceType.lightning && <FeaturesLightning />}
          {data?.typeId === DeviceType.dtp && <FeaturesDTP />}
          {isVideoDevice && <FeaturesVideo />}
          {isDitDevice && <FeaturesDit />}
          {isDetectorDevice && <FeaturesDetector />}
          {!isPhotoDevice &&
            data?.typeId !== DeviceType.lightning &&
            data?.typeId !== DeviceType.dtp &&
            data?.typeId !== DeviceType.detector &&
            !isVideoDevice &&
            !isDitDevice &&
            !isPhotosData && <Features />}
        </>
      )
    }
    if (selectedTab === DeviceTab.terminal) return <Terminal />
    return null
  }

  return (
    <>
      <CircularProgress
        color={isPageLoading ? 'white' : 'primary'}
        className={
          isPageLoading
            ? moduleClassName('show-loader')
            : moduleClassName('hide-loader')
        }
      />
      <Box
        flex={1}
        sx={{ backgroundImage: `url(${bg})`, zIndex: 100 }}
        position='absolute'
        height='100%'
        width='100%'
        display={isPageLoading ? 'block' : 'none'}
        className={moduleClassName('loader')}
      />
      <Box
        pt={{ md: 2 }}
        pb={0}
        sx={{
          paddingTop: 0,
          height: outerBoxHeight,
          width: { xss: 'unset' },
        }}
        display='flex'
        flexDirection='column'
        className={moduleClassName({ loading: isPageLoading })}
        position={
          isFullScreenGlobal || isFullScreenDetail ? 'unset' : 'relative'
        }
      >
        <Header />
        <Paper
          elevation={6}
          style={{ flex: 1 }}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.83)',
            width: '100%',
            display: isPageLoading ? 'none' : 'flex',
          }}
        >
          <Box
            flex={1}
            width='100%'
          >
            <Actions />

            <Box
              display='flex'
              flexDirection='column'
              height={contentBoxHeight}
            >
              {renderTabContent()}
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  )
}

export default DeviceModule
